import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { UnifiedItem } from "@/pages/workspaces/types";
import type { Canvas, FileRecord, InboxItem } from "@cohesive/api-client";
import { Globe } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

export interface BulkDeleteConfirmDialogProps {
  onClose: () => void;
  items: UnifiedItem[];
  onDeleteCanvas: (canvas: Canvas) => Promise<void>;
  onDeleteFileRecord: (record: FileRecord) => Promise<void>;
  onDeleteChat?: (chat: InboxItem) => Promise<void>;
  /** Runs after the bulk settles regardless of partial failures. */
  onSuccess?: () => void;
}

/**
 * Remove items whose deletion is already covered by an ancestor folder in
 * the same selection. Otherwise the ancestor's cascade (which recursively
 * deletes its descendants) races the descendant's individual delete, and
 * whichever call lands second 404s.
 */
function pruneSelectionForDelete(items: UnifiedItem[]): UnifiedItem[] {
  const folderPaths: string[] = [];
  for (const item of items) {
    if (item.kind !== "folder") continue;
    const path = (item.data as FileRecord).path;
    if (path.endsWith("/")) folderPaths.push(path);
  }
  if (folderPaths.length === 0) return items;

  return items.filter((item) => {
    if (item.kind === "canvas" || item.kind === "chat") return true;
    const path = (item.data as FileRecord).path;
    return !folderPaths.some(
      (folderPath) => folderPath !== path && path.startsWith(folderPath),
    );
  });
}

function formatBreakdown(items: UnifiedItem[]): string {
  let folders = 0;
  let files = 0;
  let canvases = 0;
  let chats = 0;
  for (const item of items) {
    if (item.kind === "folder") folders += 1;
    else if (item.kind === "file") files += 1;
    else if (item.kind === "chat") chats += 1;
    else canvases += 1;
  }
  const parts: string[] = [];
  if (canvases > 0)
    parts.push(`${canvases} ${canvases === 1 ? "canvas" : "canvases"}`);
  if (chats > 0) parts.push(`${chats} ${chats === 1 ? "chat" : "chats"}`);
  if (folders > 0)
    parts.push(`${folders} ${folders === 1 ? "folder" : "folders"}`);
  if (files > 0) parts.push(`${files} ${files === 1 ? "file" : "files"}`);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

/**
 * Confirmation dialog for bulk deletion. Deletion is permanent in the
 * current API, so we err on the side of explicit confirmation. Folders
 * delete recursively — we flag that inline when any folder is in the
 * selection.
 */
export function BulkDeleteConfirmDialog({
  onClose,
  items,
  onDeleteCanvas,
  onDeleteFileRecord,
  onDeleteChat,
  onSuccess,
}: BulkDeleteConfirmDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const count = items.length;
  const hasFolders = items.some((i) => i.kind === "folder");
  const breakdown = formatBreakdown(items);

  // Surface a separate notice when one or more selected files are
  // currently published, since deletion permanently breaks the public
  // links pointing at them. Folders are flagged in a softer "may also
  // contain" form because we don't have descendant publish state at
  // this layer of the tree.
  const publishedFileCount = items.filter(
    (i) => i.kind === "file" && (i.data as FileRecord).published,
  ).length;
  const hasPublishedFiles = publishedFileCount > 0;

  // Prune descendants of selected folders so the folder's cascade doesn't
  // race a sibling individual delete on the same row.
  const effectiveItems = useMemo(() => pruneSelectionForDelete(items), [items]);

  const handleConfirm = async () => {
    if (count === 0) return;
    setIsDeleting(true);

    // `Promise.allSettled` (not `all`) so one failing row doesn't abort
    // the rest or leave the dialog stuck open. Each handler either resolves
    // (success) or rejects (failure, counted below). Partial success is
    // fine — the workspace list refetches via the mutation's `onSettled`
    // and the user sees what did / didn't go.
    const settled = await Promise.allSettled(
      effectiveItems.map((item) => {
        if (item.kind === "canvas") return onDeleteCanvas(item.data as Canvas);
        if (item.kind === "chat")
          return onDeleteChat
            ? onDeleteChat(item.data as InboxItem)
            : Promise.resolve();
        return onDeleteFileRecord(item.data as FileRecord);
      }),
    );

    const failed = settled.filter((s) => s.status === "rejected").length;
    const ok = settled.length - failed;
    if (failed === 0) {
      toast.success(ok === 1 ? "Archived." : `Archived ${ok} items.`);
    } else if (ok === 0) {
      toast.error(
        failed === 1 ? "Failed to delete" : `Failed to delete ${failed} items`,
      );
    } else {
      toast.error(`Archived ${ok}; ${failed} failed`);
    }

    onSuccess?.();
    onClose();
    setIsDeleting(false);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isDeleting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {count === 1 ? "Archive item?" : `Archive ${count} items?`}
          </DialogTitle>
          <DialogDescription>
            {breakdown ? (
              <>
                {breakdown.charAt(0).toUpperCase() + breakdown.slice(1)}{" "}
                {hasFolders
                  ? "will be permanently deleted along with everything inside them."
                  : "will be archived and can be restored."}
              </>
            ) : (
              <>Items will be archived and can be restored.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {hasPublishedFiles && (
          <div className="flex items-start gap-2.5 rounded-md border border-lc-warning/60 bg-gold/10 p-3 text-lc-warning dark:border-lc-warning/30 dark:bg-lc-warning/10 dark:text-lc-warning">
            <Globe className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-0.5 text-sm">
              <div className="font-medium">
                {publishedFileCount === 1
                  ? hasFolders
                    ? "1 selected file is published"
                    : count === 1
                      ? "This file is published"
                      : "1 selected file is published"
                  : `${publishedFileCount} selected files are published`}
              </div>
              <div className="text-xs opacity-90">
                Deleting will permanently break any public link pointing at
                {publishedFileCount === 1 && count === 1 ? " it" : " them"}.
                {hasFolders
                  ? " Files inside selected folders may also be published."
                  : ""}
              </div>
            </div>
          </div>
        )}

        {hasFolders && !hasPublishedFiles && (
          <div className="flex items-start gap-2.5 rounded-md border border-lc-warning/60 bg-gold/10 p-3 text-lc-warning dark:border-lc-warning/30 dark:bg-lc-warning/10 dark:text-lc-warning">
            <Globe className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-0.5 text-sm">
              <div className="font-medium">
                Selected folders may contain published files
              </div>
              <div className="text-xs opacity-90">
                Any public links pointing at files inside these folders will
                permanently break.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting || count === 0}
          >
            {isDeleting ? "Archiving…" : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
